import React, { useRef, useState } from 'react';

const SCREENSHOTS = [
  { file: 'screenshot1.png', title: 'Your next whitetail hunt, at a glance', alt: 'Home screen with upcoming hunting windows, ratings and weather conditions.' },
  { file: 'screenshot2.png', title: 'Plan your next hunt with a 7-day outlook', alt: 'Seven-day outlook with separate morning and evening hunting ratings.' },
  { file: 'screenshot3.png', title: 'See hunting conditions hour by hour', alt: 'Daily view with hourly hunting ratings and a weather overlay chart.' },
  { file: 'screenshot4.png', title: 'See the factors behind your hunting outlook', alt: 'Daily details showing individual indicator bars for hunting conditions.' },
  { file: 'screenshot5.png', title: "Understand what's shaping your next hunt", alt: 'Expanded daily summary explaining the conditions shaping a hunt.' },
  { file: 'screenshot6.png', title: 'Save up to 5 hunting locations with Plus', alt: 'Plus profile for managing up to five named hunting locations.' },
];

function ScreenshotCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({
      left: Math.max(0, Math.min(index, SCREENSHOTS.length - 1)) * track.clientWidth,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  return (
    <section
      className="landing-section screenshot-carousel"
      aria-labelledby="screenshots-heading"
      aria-roledescription="carousel"
    >
      <h3 id="screenshots-heading" className="landing-heading">Take a look inside</h3>
      <p className="screenshot-intro">Swipe through the app or use the arrows. Example screens show Plus features.</p>
      <div className="screenshot-viewer">
        <div className="screenshot-controls">
          <button
            type="button"
            className="btn btn-secondary"
            aria-label="Previous screenshot"
            aria-controls="screenshot-track"
            disabled={activeIndex === 0}
            onClick={() => goTo(activeIndex - 1)}
          >
            <span aria-hidden="true">&larr;</span>
          </button>
          <span role="status" aria-live="polite" aria-atomic="true">
            {activeIndex + 1} / {SCREENSHOTS.length}
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            aria-label="Next screenshot"
            aria-controls="screenshot-track"
            disabled={activeIndex === SCREENSHOTS.length - 1}
            onClick={() => goTo(activeIndex + 1)}
          >
            <span aria-hidden="true">&rarr;</span>
          </button>
        </div>
        <div
          id="screenshot-track"
          className="screenshot-track"
          ref={trackRef}
          tabIndex={0}
          role="group"
          aria-label="App screenshots. Use left and right arrow keys to browse."
          onScroll={(event) => {
            const track = event.currentTarget;
            if (track.clientWidth > 0) {
              setActiveIndex(Math.max(0, Math.min(
                SCREENSHOTS.length - 1,
                Math.round(track.scrollLeft / track.clientWidth),
              )));
            }
          }}
          onKeyDown={(event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            if (event.key === 'Home') goTo(0);
            else if (event.key === 'End') goTo(SCREENSHOTS.length - 1);
            else goTo(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
          }}
        >
          {SCREENSHOTS.map((screenshot, index) => (
            <figure
              key={screenshot.file}
              className="screenshot-slide"
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${SCREENSHOTS.length}: ${screenshot.title}`}
            >
              <img
                src={`/screenshots/${screenshot.file}`}
                alt={screenshot.alt}
                loading="lazy"
                decoding="async"
                draggable={false}
              />
              <figcaption>{screenshot.title}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ScreenshotCarousel;
